'use client';

import type {TFunction} from 'i18next';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import {useEffect} from 'react';
import {initReactI18next, useTranslation as useTranslationOrg} from 'react-i18next';

import {TRANSLATIONS_URLS} from '@/utils/constants/api-urls';
import axios from '@/utils/helpers/axios';

import {getOptions, i18nSupportedLanguages} from './settings';

const runsOnServerSide = typeof window === 'undefined';

// on client side the normal singleton is ok
i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(async (language: string, namespace: string) => {
      const response = await axios.get(TRANSLATIONS_URLS.GET_TRANSLATIONS(language, namespace));
      return response.data.data;
    }),
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['path', 'htmlTag', 'cookie', 'navigator'],
    },
    preload: runsOnServerSide ? i18nSupportedLanguages : [],
  });

export function useTranslation(lng: string, ns: string, options?: Record<string, any>) {
  const ret = useTranslationOrg(ns, options);
  const {i18n} = ret;
  if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  }
  useEffect(() => {
    if (!lng || i18n.resolvedLanguage === lng) return;
    i18n.changeLanguage(lng);
  }, [lng]);
  return ret;
}

export function parseValue(input: string, data: Record<string, string> = {}) {
  const output: string[] = [];
  input.split('${').forEach((val) => {
    const valArr = val.split('}');
    if (valArr.length == 2 && data[valArr[0].trim()]) {
      output.push(data[valArr[0]]);
      output.push(valArr[1]);
    } else {
      output.push(val);
    }
  });
  return output.join('');
}

export function getTranslationValue(t: TFunction<string, undefined>, key: string, defaultValue: string, data: Record<string, string> = {}) {
  let value = t(key);
  if (key === value) {
    value = defaultValue;
  }
  return parseValue(value, data);
}
